import { useCallback, useEffect, useRef } from 'react';
import { create } from 'zustand';

/**
 * A small, typesafe cache-first data layer on top of Zustand.
 *
 * Behaviour:
 * - Cached data is returned immediately; a request always runs in the background.
 * - Concurrent requests for the same key are de-duplicated.
 * - Background results are deep-compared with the cached value; state is only
 *   written when it actually changed, so unchanged data causes no re-render.
 * - Components select only the fields they need, so unrelated updates don't
 *   re-render them.
 *
 * Use `useResource` directly, or wrap it in a per-resource hook (see
 * `lib/api-hooks.ts`).
 */

export type ResourceStatus = 'loading' | 'success' | 'error';

interface ResourceEntry<T> {
  status: ResourceStatus;
  data?: T;
  error?: unknown;
}

interface ResourceStoreState {
  entries: Record<string, ResourceEntry<unknown>>;
}

const useResourceStore = create<ResourceStoreState>(() => ({ entries: {} }));

/** In-flight requests, de-duplicated per key. */
const inFlight = new Map<string, Promise<unknown>>();

/** Bumped by `clearResourceCache` so stale in-flight writes are discarded. */
let cacheGeneration = 0;

/** Mounted revalidators, grouped by key. */
const revalidators = new Map<string, Set<() => void>>();

function getEntry<T>(key: string): ResourceEntry<T> | undefined {
  return useResourceStore.getState().entries[key] as ResourceEntry<T> | undefined;
}

function setEntry<T>(key: string, entry: ResourceEntry<T>): void {
  useResourceStore.setState((state) => ({
    entries: { ...state.entries, [key]: entry as ResourceEntry<unknown> },
  }));
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;

  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
    return false;
  }

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }

  const aRecord = a as Record<string, unknown>;
  const bRecord = b as Record<string, unknown>;
  const aKeys = Object.keys(aRecord);
  const bKeys = Object.keys(bRecord);

  if (aKeys.length !== bKeys.length) return false;

  return aKeys.every((key) => Object.hasOwn(bRecord, key) && deepEqual(aRecord[key], bRecord[key]));
}

/**
 * Revalidate every mounted resource whose key starts with `prefix`
 * (or all of them when no prefix is given).
 */
export function invalidateResource(prefix?: string): void {
  for (const [key, subscribers] of revalidators) {
    if (prefix && !key.startsWith(prefix)) continue;
    for (const revalidate of subscribers) revalidate();
  }
}

/** Drops the entire cache (e.g. on sign-out) and invalidates in-flight writes. */
export function clearResourceCache(): void {
  cacheGeneration += 1;
  inFlight.clear();
  useResourceStore.setState({ entries: {} });
}

export interface ResourceOptions {
  /** Skip fetching entirely (e.g. when a required param is missing). */
  enabled?: boolean;
}

export interface ResourceResult<T> {
  data: T | undefined;
  error: unknown;
  isLoading: boolean;
  refetch: () => Promise<T | undefined>;
  mutate: (updater: T | ((current: T | undefined) => T)) => void;
}

export function useResource<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: ResourceOptions = {},
): ResourceResult<T> {
  const { enabled = true } = options;

  const status = useResourceStore(
    (state) => (state.entries[key] as ResourceEntry<T> | undefined)?.status,
  );
  const data = useResourceStore(
    (state) => (state.entries[key] as ResourceEntry<T> | undefined)?.data,
  );
  const error = useResourceStore(
    (state) => (state.entries[key] as ResourceEntry<T> | undefined)?.error,
  );

  const fetcherRef = useRef(fetcher);
  useEffect(() => {
    fetcherRef.current = fetcher;
  });

  const run = useCallback(async (): Promise<T | undefined> => {
    const existing = inFlight.get(key) as Promise<T> | undefined;
    if (existing) return existing;

    const generation = cacheGeneration;
    const current = getEntry<T>(key);
    const promise = fetcherRef.current();
    inFlight.set(key, promise);

    // Only flip to loading when we have nothing to show yet.
    if (current?.data === undefined && current?.status !== 'loading') {
      setEntry<T>(key, { status: 'loading', data: current?.data });
    }

    try {
      const next = await promise;
      if (generation !== cacheGeneration) return next;

      const previous = getEntry<T>(key);

      if (!deepEqual(previous?.data, next)) {
        setEntry<T>(key, { status: 'success', data: next });
      } else if (previous?.status !== 'success') {
        setEntry<T>(key, { status: 'success', data: previous?.data });
      }

      return next;
    } catch (caught) {
      if (generation !== cacheGeneration) throw caught;

      const previous = getEntry<T>(key);
      if (previous?.status !== 'error' || previous.error !== caught) {
        setEntry<T>(key, { status: 'error', data: previous?.data, error: caught });
      }
      throw caught;
    } finally {
      inFlight.delete(key);
    }
  }, [key]);

  // Fetch on mount / when the key changes.
  useEffect(() => {
    if (!enabled) return;
    void run().catch(() => {});
  }, [enabled, run]);

  // Register for invalidation-driven revalidation.
  useEffect(() => {
    if (!enabled) return;

    let subscribers = revalidators.get(key);
    if (!subscribers) {
      subscribers = new Set();
      revalidators.set(key, subscribers);
    }
    subscribers.add(run);

    return () => {
      subscribers?.delete(run);
      if (subscribers && subscribers.size === 0) revalidators.delete(key);
    };
  }, [key, enabled, run]);

  const refetch = useCallback(() => run(), [run]);

  const mutate = useCallback(
    (updater: T | ((current: T | undefined) => T)) => {
      const current = getEntry<T>(key)?.data;
      const next =
        typeof updater === 'function' ? (updater as (value: T | undefined) => T)(current) : updater;
      setEntry<T>(key, { status: 'success', data: next });
    },
    [key],
  );

  return {
    data,
    error,
    isLoading: enabled && status !== 'success' && status !== 'error',
    refetch,
    mutate,
  };
}
